import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { useBranding, FONT_OPTIONS } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { FloppyDisk, UploadSimple, Trash, PaintBrush, ArrowCounterClockwise } from "@phosphor-icons/react";
import ContrastBadge from "./ContrastBadge";
import FontPicker from "./FontPicker";

const DEFAULTS = {
  brand_firm_name: "Barnard Inc",
  brand_tagline: "Lead Tracker",
  brand_primary_color: "#0F172A",
  brand_accent_color: "#002FA7",
  brand_sidebar_bg_color: "#FFFFFF",
  brand_sidebar_text_color: "#0F172A",
  brand_header_bg_color: "#FFFFFF",
  brand_header_text_color: "#0F172A",
  brand_font_display: "Cormorant Garamond",
  brand_font_body: "IBM Plex Sans",
  brand_logo_url: null,
  brand_login_image_url: null,
};

const BRAND_TO_CSS = {
  brand_primary_color: "primary_color",
  brand_accent_color: "accent_color",
  brand_sidebar_bg_color: "sidebar_bg_color",
  brand_sidebar_text_color: "sidebar_text_color",
  brand_header_bg_color: "header_bg_color",
  brand_header_text_color: "header_text_color",
  brand_font_display: "font_display",
  brand_font_body: "font_body",
  brand_firm_name: "firm_name",
  brand_tagline: "tagline",
  brand_logo_url: "logo_url",
  brand_login_image_url: "login_image_url",
};

export default function BrandingTab({ settings, onSaved }) {
  const { branding, refresh: refreshBranding, update: updateBranding } = useBranding();
  const [form, setForm] = useState({
    brand_firm_name: settings.brand_firm_name || DEFAULTS.brand_firm_name,
    brand_tagline: settings.brand_tagline || DEFAULTS.brand_tagline,
    brand_primary_color: settings.brand_primary_color || DEFAULTS.brand_primary_color,
    brand_accent_color: settings.brand_accent_color || DEFAULTS.brand_accent_color,
    brand_sidebar_bg_color: settings.brand_sidebar_bg_color || DEFAULTS.brand_sidebar_bg_color,
    brand_sidebar_text_color: settings.brand_sidebar_text_color || DEFAULTS.brand_sidebar_text_color,
    brand_header_bg_color: settings.brand_header_bg_color || DEFAULTS.brand_header_bg_color,
    brand_header_text_color: settings.brand_header_text_color || DEFAULTS.brand_header_text_color,
    brand_font_display: settings.brand_font_display || DEFAULTS.brand_font_display,
    brand_font_body: settings.brand_font_body || DEFAULTS.brand_font_body,
    // Images are no longer returned in /settings (too large) — read the
    // current ones from the cached branding context.
    brand_logo_url: branding?.logo_url || null,
    brand_login_image_url: branding?.login_image_url || null,
  });
  const [saving, setSaving] = useState(false);
  const logoRef = useRef(null);
  const loginImgRef = useRef(null);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    const cssKey = BRAND_TO_CSS[k];
    if (cssKey) updateBranding({ [cssKey]: v });
  };

  const onUpload = async (kind, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image must be under 2 MB");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/branding/upload?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const field = kind === "logo" ? "brand_logo_url" : "brand_login_image_url";
      setField(field, data.url);
      toast.success(`${kind === "logo" ? "Logo" : "Login image"} uploaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await api.patch("/settings", form);
      toast.success("Branding saved");
      refreshBranding();
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onReset = async () => {
    if (!window.confirm("Reset all branding to defaults?")) return;
    setForm(DEFAULTS);
    try {
      await api.patch("/settings", DEFAULTS);
      toast.success("Branding reset");
      refreshBranding();
      onSaved();
    } catch (e) {
      toast.error("Reset failed");
    }
  };

  return (
    <div className="space-y-8">
      <section className="bg-card border border-border rounded-sm p-6 space-y-5">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-sm flex items-center justify-center shrink-0" style={{ backgroundColor: "var(--brand-accent)" }}>
            <PaintBrush size={24} weight="fill" className="text-white" />
          </div>
          <div>
            <h2 className="font-display text-2xl text-foreground">Branding</h2>
            <p className="text-sm text-muted-foreground mt-1">Customize the look and feel — colors, fonts, logos, theme. Changes preview live across the app.</p>
          </div>
        </div>
      </section>

      {/* Identity */}
      <section className="bg-card border border-border rounded-sm p-6 space-y-5">
        <div className="label-tracking">Identity</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="label-tracking">Firm name</Label>
            <Input data-testid="brand-firm-name" value={form.brand_firm_name} onChange={(e) => setField("brand_firm_name", e.target.value)} className="mt-2 rounded-sm h-10" />
          </div>
          <div>
            <Label className="label-tracking">Tagline</Label>
            <Input data-testid="brand-tagline" value={form.brand_tagline} onChange={(e) => setField("brand_tagline", e.target.value)} className="mt-2 rounded-sm h-10" />
          </div>
        </div>
      </section>

      {/* Logos & images */}
      <section className="bg-card border border-border rounded-sm p-6 space-y-6">
        <div className="label-tracking">Logos & Imagery</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Logo */}
          <div>
            <Label className="label-tracking">Sidebar Logo</Label>
            <p className="text-[11px] text-muted-foreground mt-1">Square works best. PNG with transparent background. Max 2 MB.</p>
            <div className="mt-3 flex items-center gap-4">
              <div
                className="h-20 w-20 rounded-sm border border-border flex items-center justify-center overflow-hidden"
                style={{ backgroundColor: "var(--brand-primary)" }}
              >
                {form.brand_logo_url ? (
                  <img src={form.brand_logo_url} alt="Logo" className="h-full w-full object-cover" />
                ) : (
                  <span className="text-white text-xs">No logo</span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input
                  ref={logoRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => onUpload("logo", e.target.files?.[0])}
                  data-testid="logo-file-input"
                />
                <Button data-testid="upload-logo-btn" type="button" variant="outline" size="sm" className="rounded-sm gap-2" onClick={() => logoRef.current?.click()}>
                  <UploadSimple size={14} /> Upload logo
                </Button>
                {form.brand_logo_url && (
                  <Button data-testid="remove-logo-btn" type="button" variant="ghost" size="sm" className="rounded-sm gap-2 text-rose-600 hover:bg-rose-50 hover:text-rose-700" onClick={() => setField("brand_logo_url", null)}>
                    <Trash size={14} /> Remove
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Login image */}
          <div>
            <Label className="label-tracking">Login Page Image</Label>
            <p className="text-[11px] text-muted-foreground mt-1">Wide hero image shown on the login screen. 16:9 or larger.</p>
            <div className="mt-3 flex items-center gap-4">
              <div className="h-20 w-32 rounded-sm border border-border bg-muted overflow-hidden">
                {form.brand_login_image_url ? (
                  <img src={form.brand_login_image_url} alt="Login" className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-[11px] text-muted-foreground">Default</div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input
                  ref={loginImgRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => onUpload("login_image", e.target.files?.[0])}
                  data-testid="login-image-input"
                />
                <Button data-testid="upload-login-image-btn" type="button" variant="outline" size="sm" className="rounded-sm gap-2" onClick={() => loginImgRef.current?.click()}>
                  <UploadSimple size={14} /> Upload image
                </Button>
                {form.brand_login_image_url && (
                  <Button data-testid="remove-login-image-btn" type="button" variant="ghost" size="sm" className="rounded-sm gap-2 text-rose-600 hover:bg-rose-50 hover:text-rose-700" onClick={() => setField("brand_login_image_url", null)}>
                    <Trash size={14} /> Remove
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Colors */}
      <section className="bg-card border border-border rounded-sm p-6 space-y-6">
        <div>
          <div className="label-tracking">Colors</div>
          <p className="text-xs text-muted-foreground mt-2">Pick any pair of colors. The system checks WCAG contrast and warns if text will be hard to read — click the "use white/black" button to auto-fix.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <ColorField
            label="Primary (active nav / buttons)"
            testIdBase="primary"
            value={form.brand_primary_color}
            onChange={(v) => setField("brand_primary_color", v)}
          />
          <ColorField
            label="Accent (links / highlights)"
            testIdBase="accent"
            value={form.brand_accent_color}
            onChange={(v) => setField("brand_accent_color", v)}
          />
        </div>

        <div className="pt-4 border-t border-border">
          <div className="label-tracking mb-3">Sidebar</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <ColorField
              label="Sidebar background"
              testIdBase="sidebar-bg"
              value={form.brand_sidebar_bg_color}
              onChange={(v) => setField("brand_sidebar_bg_color", v)}
            />
            <ColorField
              label="Sidebar text"
              testIdBase="sidebar-text"
              value={form.brand_sidebar_text_color}
              onChange={(v) => setField("brand_sidebar_text_color", v)}
            />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <ContrastBadge
              label="Sidebar text on background"
              fg={form.brand_sidebar_text_color}
              bg={form.brand_sidebar_bg_color}
              onAutoFix={(v) => setField("brand_sidebar_text_color", v)}
            />
            <ContrastBadge
              label="Active nav (white on Primary)"
              fg="#FFFFFF"
              bg={form.brand_primary_color}
              onAutoFix={(v) => setField("brand_primary_color", v === "#FFFFFF" ? form.brand_primary_color : "#0F172A")}
            />
          </div>
          <SidebarPreview form={form} />
        </div>

        <div className="pt-4 border-t border-border">
          <div className="label-tracking mb-3">Top Header</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <ColorField
              label="Header background"
              testIdBase="header-bg"
              value={form.brand_header_bg_color}
              onChange={(v) => setField("brand_header_bg_color", v)}
            />
            <ColorField
              label="Header text"
              testIdBase="header-text"
              value={form.brand_header_text_color}
              onChange={(v) => setField("brand_header_text_color", v)}
            />
          </div>
          <div className="mt-3">
            <ContrastBadge
              label="Header text on background"
              fg={form.brand_header_text_color}
              bg={form.brand_header_bg_color}
              onAutoFix={(v) => setField("brand_header_text_color", v)}
            />
          </div>
          <HeaderPreview form={form} />
        </div>
      </section>

      {/* Fonts */}
      <section className="bg-card border border-border rounded-sm p-6 space-y-5">
        <div className="label-tracking">Typography</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="label-tracking">Display Font (headings)</Label>
            <FontPicker
              testId="font-display-picker"
              value={form.brand_font_display}
              options={FONT_OPTIONS.display}
              onChange={(v) => setField("brand_font_display", v)}
              placeholder="Search heading fonts…"
            />
          </div>
          <div>
            <Label className="label-tracking">Body Font (paragraphs, labels, UI)</Label>
            <FontPicker
              testId="font-body-picker"
              value={form.brand_font_body}
              options={FONT_OPTIONS.body}
              onChange={(v) => setField("brand_font_body", v)}
              placeholder="Search body fonts…"
            />
          </div>
        </div>

        {/* Realistic typography preview - mirrors a real Dashboard heading */}
        <div className="mt-2 rounded-sm border border-border bg-muted/40 p-6 sm:p-8" data-testid="typography-preview">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-4">Live preview — Dashboard heading</div>
          <div
            className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground"
            style={{ fontFamily: form.brand_font_body }}
          >
            Overview
          </div>
          <h2
            className="text-4xl sm:text-5xl tracking-tight leading-none mt-2 text-foreground"
            style={{ fontFamily: form.brand_font_display }}
          >
            Lead Operations.
          </h2>
          <p
            className="text-sm text-muted-foreground mt-3 max-w-2xl leading-relaxed"
            style={{ fontFamily: form.brand_font_body }}
          >
            Real-time view of every enquiry arriving at{" "}
            <span className="font-mono text-foreground">Leads@barnardinc.co.za</span>, intelligently parsed and routed.
          </p>
          <div className="mt-6 flex items-center gap-2 text-[11px] text-muted-foreground">
            <span style={{ fontFamily: form.brand_font_display }} className="text-lg text-foreground">Aa</span>
            <span style={{ fontFamily: form.brand_font_body }}>Display: {form.brand_font_display}</span>
            <span className="opacity-50">·</span>
            <span style={{ fontFamily: form.brand_font_body }}>Body: {form.brand_font_body}</span>
          </div>
        </div>
      </section>

      {/* Actions */}
      <div className="flex justify-between gap-3 pt-2">
        <Button data-testid="reset-branding-btn" variant="outline" onClick={onReset} className="rounded-sm gap-2 text-rose-600 hover:bg-rose-50 hover:text-rose-700">
          <ArrowCounterClockwise size={14} /> Reset to defaults
        </Button>
        <Button data-testid="save-branding-btn" onClick={onSave} disabled={saving} className="rounded-sm gap-2" style={{ backgroundColor: "var(--brand-primary)" }}>
          <FloppyDisk size={14} /> {saving ? "Saving…" : "Save branding"}
        </Button>
      </div>
    </div>
  );
}

// ---------- Helper sub-components ----------

function ColorField({ label, value, onChange, testIdBase }) {
  return (
    <div>
      <Label className="label-tracking">{label}</Label>
      <div className="mt-2 flex items-center gap-3">
        <input
          data-testid={`${testIdBase}-color-picker`}
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-14 rounded-sm border border-border cursor-pointer"
        />
        <Input
          data-testid={`${testIdBase}-color-hex`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="rounded-sm h-10 font-mono text-xs uppercase"
        />
      </div>
    </div>
  );
}

function SidebarPreview({ form }) {
  return (
    <div className="mt-4 rounded-sm border border-border overflow-hidden" data-testid="sidebar-preview">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground px-3 py-1.5 bg-muted">Sidebar preview</div>
      <div
        className="p-4 space-y-2"
        style={{ backgroundColor: form.brand_sidebar_bg_color, color: form.brand_sidebar_text_color }}
      >
        <div className="text-sm font-medium" style={{ fontFamily: form.brand_font_display }}>{form.brand_firm_name}</div>
        <div
          className="text-xs px-3 py-2 rounded-sm inline-block text-white"
          style={{ backgroundColor: form.brand_primary_color }}
        >
          Active nav item
        </div>
        <div className="text-xs opacity-75">Inactive nav item</div>
      </div>
    </div>
  );
}

function HeaderPreview({ form }) {
  return (
    <div className="mt-4 rounded-sm border border-border overflow-hidden" data-testid="header-preview">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground px-3 py-1.5 bg-muted">Header preview</div>
      <div
        className="h-12 px-4 flex items-center"
        style={{ backgroundColor: form.brand_header_bg_color, color: form.brand_header_text_color }}
      >
        <div className="text-[11px] uppercase tracking-[0.18em]" style={{ opacity: 0.7 }}>Dashboard</div>
        <div className="ml-auto text-xs">mark@barnardinc.co.za</div>
      </div>
    </div>
  );
}

